import React, { useState } from 'react';
import {
  Box,
  Container,
  Typography,
  TextField,
  MenuItem,
  Button,
  Accordion,
  AccordionSummary,
  AccordionDetails,
} from '@mui/material';
import { COLORS, FONTS } from '../theme';
import { SCHOOL, STEPS, FAQS } from '../data/siteData';
import { Section, SectionHeading, Reveal, Icon, float, pulse } from './common';

const AGE_OPTIONS = ['1.5 to 2.5 years', '2.5 to 3.5 years', '3.5 to 5 years'];

const fieldSx = {
  '& .MuiOutlinedInput-root': {
    borderRadius: '20px',
    bgcolor: '#fff',
    fontWeight: 700,
    '& fieldset': { borderWidth: 3, borderColor: 'rgba(255,255,255,.0)' },
    '&:hover fieldset': { borderColor: COLORS.yellow },
    '&.Mui-focused fieldset': { borderColor: COLORS.yellow },
  },
  '& .MuiInputLabel-root': { fontWeight: 700 },
};

export default function Admission() {
  const [form, setForm] = useState({ parent: '', phone: '', age: '', message: '' });
  const handle = (key) => (e) => setForm({ ...form, [key]: e.target.value });

  const submit = (e) => {
    e.preventDefault();
    const lines = [
      "Hello St Lord's Day Care, I would like to enquire about admission.",
      `Parent name: ${form.parent}`,
      `Phone: ${form.phone}`,
      `Child age: ${form.age}`,
      form.message ? `Message: ${form.message}` : '',
    ].filter(Boolean);
    const url = `https://wa.me/${SCHOOL.phoneRaw}?text=${encodeURIComponent(lines.join('\n'))}`;
    window.open(url, '_blank', 'noopener,noreferrer');
  };

  return (
    <Section id="admission" bg={COLORS.navy} sx={{ pt: { xs: 2, md: 4 }, pb: { xs: 8, md: 12 } }}>
      <Box sx={{ position: 'absolute', top: 40, left: '5%', width: 80, height: 80, borderRadius: '50%', bgcolor: `${COLORS.pink}55`, animation: `${float} 6s ease-in-out infinite` }} />
      <Box sx={{ position: 'absolute', bottom: 60, right: '6%', width: 110, height: 110, borderRadius: '30px', bgcolor: `${COLORS.yellow}44`, transform: 'rotate(20deg)', animation: `${float} 8s ease-in-out infinite` }} />

      <Container maxWidth="lg" sx={{ position: 'relative', zIndex: 2 }}>
        <SectionHeading
          eyebrow="Admission Open"
          title="Join the St Lord's Family"
          subtitle="Admissions are open for children from 1.5 years to 5 years. Enquire today and book a visit to our campus."
          color={COLORS.yellow}
          light
        />

        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', md: 'repeat(4, 1fr)' }, gap: 3, mb: { xs: 8, md: 10 } }}>
          {STEPS.map((s, i) => (
            <Reveal key={s.title} direction="up" delay={i * 120} sx={{ height: '100%' }}>
              <Box sx={{ position: 'relative', height: '100%', bgcolor: '#fff', borderRadius: '28px', p: 3, pt: 5, textAlign: 'center', border: `4px solid ${s.color}` }}>
                <Box sx={{ position: 'absolute', top: -28, left: '50%', ml: '-28px', width: 56, height: 56, borderRadius: '50%', bgcolor: s.color, color: '#fff', border: '5px solid #fff', display: 'flex', alignItems: 'center', justifyContent: 'center', fontFamily: FONTS.heading, fontWeight: 800, fontSize: 26 }}>
                  {i + 1}
                </Box>
                <Typography variant="h5" sx={{ color: s.color, fontSize: 24 }}>{s.title}</Typography>
                <Typography sx={{ mt: 1, fontSize: 15, lineHeight: 1.6 }}>{s.text}</Typography>
              </Box>
            </Reveal>
          ))}
        </Box>

        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1.1fr' }, gap: { xs: 6, md: 6 }, alignItems: 'start' }}>
          <Reveal direction="left">
            <Box sx={{ position: 'relative', bgcolor: '#8B5A2B', backgroundImage: 'repeating-linear-gradient(90deg, rgba(0,0,0,.08) 0 3px, transparent 3px 22px)', borderRadius: '30px', p: { xs: 3, md: 4 }, border: '8px solid #6B3F17', boxShadow: '0 16px 0 rgba(0,0,0,.25)', color: '#fff' }}>
              <Typography variant="h3" sx={{ fontSize: { xs: 36, md: 48 }, color: COLORS.yellow, lineHeight: 1 }}>Admission Open</Typography>
              <Typography sx={{ mt: 1.5, fontSize: 17, lineHeight: 1.7 }}>
                Day Care for children aged {SCHOOL.ages}. Timings {SCHOOL.timings}.
              </Typography>
              <Box sx={{ display: 'grid', gap: 1.4, mt: 3 }}>
                {['Play', 'Learn', 'Make Friends', 'Build Confidence'].map((t) => (
                  <Box key={t} sx={{ display: 'flex', alignItems: 'center', gap: 1.2 }}>
                    <Box sx={{ width: 30, height: 30, borderRadius: '50%', bgcolor: COLORS.green, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                      <Icon name="check" sx={{ fontSize: 20 }} />
                    </Box>
                    <Typography sx={{ fontFamily: FONTS.heading, fontWeight: 800, fontSize: 22 }}>{t}</Typography>
                  </Box>
                ))}
              </Box>
              <Button
                href={`https://wa.me/${SCHOOL.phoneRaw}`}
                target="_blank"
                rel="noopener noreferrer"
                variant="contained"
                fullWidth
                startIcon={<Icon name="phone" />}
                sx={{ mt: 4, py: 1.5, fontSize: 21, color: COLORS.navy, bgcolor: COLORS.yellow, boxShadow: '0 7px 0 #E0A800', animation: `${pulse} 2.5s ease-in-out infinite`, '&:hover': { bgcolor: '#ffd35e' } }}
              >
                {SCHOOL.phone}
              </Button>
            </Box>
          </Reveal>

          <Reveal direction="right">
            <Box component="form" onSubmit={submit} sx={{ bgcolor: COLORS.pink, borderRadius: '30px', p: { xs: 3, md: 4 }, boxShadow: '0 16px 0 rgba(0,0,0,.25)' }}>
              <Typography variant="h4" sx={{ color: '#fff', fontSize: { xs: 28, md: 34 }, mb: 0.5 }}>Admission Enquiry</Typography>
              <Typography sx={{ color: 'rgba(255,255,255,.92)', mb: 3, fontSize: 15 }}>
                Fill in the details and we will open WhatsApp with your message ready to send.
              </Typography>
              <Box sx={{ display: 'grid', gap: 2 }}>
                <TextField label="Parent name" value={form.parent} onChange={handle('parent')} required fullWidth sx={fieldSx} />
                <TextField label="Phone number" value={form.phone} onChange={handle('phone')} required fullWidth type="tel" sx={fieldSx} />
                <TextField select label="Child age" value={form.age} onChange={handle('age')} required fullWidth sx={fieldSx}>
                  {AGE_OPTIONS.map((o) => (
                    <MenuItem key={o} value={o}>{o}</MenuItem>
                  ))}
                </TextField>
                <TextField label="Message (optional)" value={form.message} onChange={handle('message')} fullWidth multiline minRows={3} sx={fieldSx} />
                <Button
                  type="submit"
                  variant="contained"
                  size="large"
                  endIcon={<Icon name="arrow" />}
                  sx={{ py: 1.5, fontSize: 20, bgcolor: COLORS.navy, boxShadow: '0 7px 0 #0F1948', '&:hover': { bgcolor: '#26398c', transform: 'translateY(3px)', boxShadow: '0 4px 0 #0F1948' }, transition: 'all .2s ease' }}
                >
                  Send Enquiry
                </Button>
              </Box>
            </Box>
          </Reveal>
        </Box>

        <Box sx={{ maxWidth: 820, mx: 'auto', mt: { xs: 9, md: 12 } }}>
          <Reveal>
            <Typography variant="h3" sx={{ color: '#fff', textAlign: 'center', fontSize: { xs: 30, md: 40 }, mb: 4 }}>
              Questions Parents Ask
            </Typography>
          </Reveal>
          {FAQS.map((f, i) => (
            <Reveal key={f.q} direction="up" delay={i * 80}>
              <Accordion
                disableGutters
                elevation={0}
                square
                sx={{ mb: 1.6, borderRadius: '22px !important', overflow: 'hidden', '&::before': { display: 'none' }, bgcolor: '#fff' }}
              >
                <AccordionSummary
                  expandIcon={<Icon name="plus" sx={{ color: COLORS.pink }} />}
                  sx={{ px: 3, py: 0.5, '& .MuiAccordionSummary-content': { my: 1.5 } }}
                >
                  <Typography sx={{ fontFamily: FONTS.heading, fontWeight: 800, fontSize: 19, color: COLORS.navy }}>{f.q}</Typography>
                </AccordionSummary>
                <AccordionDetails sx={{ px: 3, pb: 2.5 }}>
                  <Typography sx={{ fontSize: 16, lineHeight: 1.7 }}>{f.a}</Typography>
                </AccordionDetails>
              </Accordion>
            </Reveal>
          ))}
        </Box>
      </Container>
    </Section>
  );
}
